/*
 * Pro mega menu — desktop behaviour for <pro-mega-menu> (snippets/pro-mega-menu.liquid).
 * - Hover intent (when "Open menu with hover" is on) and click/keyboard toggling
 * - Instant switching between panels, animated close, Escape / outside click to close
 * - Live preview swap with image crossfade, preloaded after the first open
 * - Page overlay and solid header while a panel is open on the transparent homepage header
 */
if (!customElements.get('pro-mega-menu')) {
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 220;
  const CLOSE_DURATION = 180;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktop = window.matchMedia('(min-width: 990px)');

  class ProMegaMenu extends HTMLElement {
    connectedCallback() {
      this.hoverEnabled = this.dataset.hover === 'true';
      this.overlayEnabled = this.dataset.overlay === 'true';
      this.siteHeader = document.getElementById('site-header');
      this.headerSection = this.closest('.section-header') || document.querySelector('.section-header');
      this.panels = Array.from(this.querySelectorAll('.pro-mega__details'));
      this.openTimer = null;
      this.closeTimer = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      this.panels.forEach((details) => this.bindPanel(details));
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      desktop.addEventListener('change', this.onViewportChange);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.onViewportChange);
      desktop.removeEventListener('change', this.onViewportChange);
      this.setActive(false);
      this.overlay?.remove();
    }

    bindPanel(details) {
      const summary = details.querySelector('summary');
      const item = details.closest('li');

      summary.addEventListener('click', (event) => this.onSummaryClick(event, details));
      details.addEventListener('toggle', () => this.onToggle(details));

      if (this.hoverEnabled) {
        item.addEventListener('mouseenter', () => this.onPointerEnter(details));
        item.addEventListener('mouseleave', () => this.onPointerLeave(details));
      }

      details.querySelectorAll('[data-pm-link]').forEach((link) => {
        link.addEventListener('mouseenter', () => this.preview(details, link));
        link.addEventListener('focus', () => this.preview(details, link));
      });
    }

    get openPanel() {
      return this.panels.find((details) => details.open && !details.classList.contains('is-closing'));
    }

    canHover() {
      return this.hoverEnabled && finePointer.matches && desktop.matches;
    }

    onSummaryClick(event, details) {
      event.preventDefault();
      const isOpen = details === this.openPanel;

      // Once a panel was opened by hovering, a mouse click on its title goes to the collection page.
      // Keyboard activation (event.detail === 0) always toggles.
      if (isOpen && event.detail > 0 && details.dataset.openedBy === 'hover' && this.canHover()) {
        const href = details.querySelector('summary').dataset.href;
        if (href) {
          window.location.href = href;
          return;
        }
      }

      isOpen ? this.close(details) : this.open(details, event.detail === 0 ? 'keyboard' : 'click');
    }

    onPointerEnter(details) {
      if (!this.canHover()) return;
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      if (details === this.openPanel) return;
      this.openTimer = setTimeout(() => this.open(details, 'hover'), this.openPanel ? 0 : OPEN_DELAY);
    }

    onPointerLeave(details) {
      if (!this.canHover()) return;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (details === this.openPanel && !details.closest('li').matches(':hover')) this.close(details);
      }, CLOSE_DELAY);
    }

    open(details, source) {
      clearTimeout(this.closeTimer);
      clearTimeout(details.closeTimeout);
      const previous = this.openPanel;
      details.dataset.openedBy = source;
      if (previous === details) return;

      this.syncGeometry();
      details.classList.remove('is-closing');
      // Switching from another open panel: show the new one at once instead of replaying the entrance.
      details.classList.toggle('pm-instant', Boolean(previous));
      details.open = true;
      details.querySelector('summary').setAttribute('aria-expanded', 'true');
      if (previous) this.close(previous, false);

      this.setActive(true);
      this.warmPreview(details);
    }

    close(details, animate = true) {
      if (!details.open) return;
      clearTimeout(details.closeTimeout);
      delete details.dataset.openedBy;
      details.querySelector('summary').setAttribute('aria-expanded', 'false');

      const finish = () => {
        details.classList.remove('is-closing', 'pm-instant');
        details.open = false;
        if (!this.openPanel) this.setActive(false);
      };

      if (!animate || reducedMotion.matches) {
        finish();
        return;
      }

      details.classList.add('is-closing');
      if (!this.openPanel) this.setActive(false);
      details.closeTimeout = setTimeout(finish, CLOSE_DURATION);
    }

    closeAll(animate = true) {
      clearTimeout(this.openTimer);
      this.panels.forEach((details) => this.close(details, animate));
    }

    // Keeps overlay/header state right when something else closes a panel (e.g. header-menu focusout).
    onToggle(details) {
      if (details.open) return;
      details.classList.remove('is-closing', 'pm-instant');
      details.querySelector('summary').setAttribute('aria-expanded', 'false');
      if (!this.openPanel) this.setActive(false);
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const details = this.openPanel;
      if (!details) return;
      this.close(details);
      details.querySelector('summary').focus();
    }

    onDocumentClick(event) {
      if (!this.openPanel || this.contains(event.target)) return;
      this.closeAll();
    }

    onViewportChange() {
      if (!desktop.matches) {
        this.closeAll(false);
        return;
      }
      if (this.openPanel) this.syncGeometry();
    }

    syncGeometry() {
      const root = document.documentElement;
      root.style.setProperty('--pm-vw', `${root.clientWidth}px`);
      // #site-header, not the section: on the transparent homepage header the section collapses to 0 height.
      const header = this.siteHeader || this.headerSection;
      if (header) root.style.setProperty('--pm-panel-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    }

    setActive(active) {
      document.body.classList.toggle('pro-mega-is-open', active);

      if (this.siteHeader) {
        const solid = active && this.siteHeader.classList.contains('site-header-transparent');
        this.siteHeader.classList.toggle('pro-mega-solid', solid);
      }

      if (!this.overlayEnabled) return;
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'pro-mega-overlay';
        this.overlay.setAttribute('aria-hidden', 'true');
        this.overlay.addEventListener('click', () => this.closeAll());
        this.overlay.addEventListener('mouseenter', () => {
          if (this.canHover() && this.openPanel) this.close(this.openPanel);
        });
        document.body.appendChild(this.overlay);
      }
      this.overlay.classList.toggle('is-visible', active);
    }

    preview(details, link) {
      const card = details.querySelector('[data-pm-preview]');
      if (!card || card.activeLink === link) return;
      card.activeLink = link;

      details.querySelectorAll('[data-pm-link].is-previewed').forEach((el) => el.classList.remove('is-previewed'));
      link.classList.add('is-previewed');

      card.setAttribute('href', link.getAttribute('href'));
      const title = card.querySelector('[data-pm-preview-title]');
      const meta = card.querySelector('[data-pm-preview-meta]');
      if (title) title.textContent = link.dataset.pmTitle || '';
      if (meta) meta.textContent = link.dataset.pmMeta || '';

      const src = link.dataset.pmSrc;
      if (!src) return;
      const layers = Array.from(card.querySelectorAll('[data-pm-layer]'));
      const current = layers.find((layer) => layer.classList.contains('is-active'));
      if (current && current.getAttribute('src') === src) return;
      const next = layers.find((layer) => layer !== current) || layers[0];

      const token = (card.swapToken = (card.swapToken || 0) + 1);
      const reveal = () => {
        if (card.swapToken !== token) return;
        next.classList.add('is-active');
        if (current && current !== next) current.classList.remove('is-active');
      };

      next.onload = reveal;
      next.onerror = null;
      next.loading = 'eager';
      next.setAttribute('src', src);
      if (next.complete && next.naturalWidth) reveal();
    }

    // After the first open, fetch every preview image in the background so later swaps are instant.
    warmPreview(details) {
      if (details.dataset.warmed) return;
      details.dataset.warmed = 'true';
      const run = () =>
        details.querySelectorAll('[data-pm-src]').forEach((link) => {
          const image = new Image();
          image.decoding = 'async';
          image.src = link.dataset.pmSrc;
        });
      'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 1200 }) : setTimeout(run, 400);
    }
  }

  customElements.define('pro-mega-menu', ProMegaMenu);
}
